// Export Engine V6 — the whole export runs on the user's device (never the server).
// Every timeline (multi-clip, trims, images, elements, backgrounds, shapes, filters,
// transitions, music/voice) is rendered with the SAME drawing rules as the editor preview,
// encoded with WebCodecs (H.264 + AAC; VP9/AV1 + Opus when a browser lacks H.264/AAC) and
// muxed to MP4 in the browser.
import { Muxer, ArrayBufferTarget } from "mp4-muxer";
import { fetchWithProgress, loadDemux, createClipReader, createSeekReader } from "./clipReader";
import { frameNeeds, drawTimelineFrame } from "./timelineFrame";
import { videoClips, imageClips, clipSourceUrl, absUrl } from "@/lib/timeline";
import { backgroundObject } from "@/lib/shapes";
import { ensureTextFonts, collectCaptionText } from "@/lib/fonts";

const SR = 48000;
const BPP = { Draft: 0.06, Standard: 0.1, High: 0.15, Maximum: 0.22 };
const VIDEO_CODECS = [
  ...["avc1.640033", "avc1.640028", "avc1.4d0028", "avc1.42002a", "avc1.42001f"].map((c) => ({ codec: c, mux: "avc", std: true })),
  { codec: "vp09.00.40.08", mux: "vp9" }, { codec: "vp09.00.10.08", mux: "vp9" }, { codec: "av01.0.08M.08", mux: "av1" },
];
const num = (v, d) => (v === undefined || v === null || v === "" || isNaN(Number(v)) ? d : Number(v));
const abortIf = (signal) => { if (signal?.aborted) throw new DOMException("Aborted", "AbortError"); };

export function canDeviceExport() {
  return typeof window !== "undefined" && "VideoEncoder" in window && "VideoDecoder" in window && "VideoFrame" in window;
}

async function pickVideo(W, H, fps, bitrate) {
  for (const v of VIDEO_CODECS) {
    const base = { codec: v.codec, width: W, height: H, bitrate, framerate: fps, ...(v.mux === "avc" ? { avc: { format: "avc" } } : {}) };
    for (const hw of ["prefer-hardware", "no-preference"]) {
      const s = await window.VideoEncoder.isConfigSupported({ ...base, hardwareAcceleration: hw }).catch(() => null);
      if (s?.supported) return { ...v, config: s.config || { ...base, hardwareAcceleration: hw }, hw: hw === "prefer-hardware" };
    }
  }
  return null;
}

async function pickAudio() {
  if (!("AudioEncoder" in window)) return null;
  for (const a of [{ codec: "mp4a.40.2", mux: "aac", std: true }, { codec: "opus", mux: "opus" }]) {
    const cfg = { codec: a.codec, sampleRate: SR, numberOfChannels: 2, bitrate: 192_000 };
    const s = await window.AudioEncoder.isConfigSupported(cfg).catch(() => null);
    if (s?.supported) return { ...a, config: cfg };
  }
  return null;
}

function timelineAudioClips(mediaTracks, videoUrl, duration) {
  if (!(mediaTracks || []).length) return videoUrl ? [{ url: videoUrl, o: { start: 0, source_start: 0, source_end: duration } }] : [];
  const out = [];
  for (const tr of mediaTracks) {
    if (tr.type !== "audio" || tr.muted || tr.hidden) continue;
    for (const o of tr.objects || []) {
      if (o.muted || o.hidden) continue;
      const url = o.asset_url ? absUrl(o.asset_url) : (!o.asset_id ? videoUrl : null);
      if (url) out.push({ url, o });
    }
  }
  return out;
}

// Mix the timeline audio on the device: trim → volume → fades → delay → (normalize/enhance/denoise) → sum.
async function renderTimelineAudio({ clips, duration, buffers }) {
  const ctx = new window.OfflineAudioContext(2, Math.max(1, Math.ceil(duration * SR)), SR);
  const decoded = new Map();
  const decode = (url) => {
    if (!decoded.has(url)) {
      decoded.set(url, (async () => {
        try {
          const ab = buffers.get(url) ? buffers.get(url).slice(0) : await (await fetch(url)).arrayBuffer();
          return await ctx.decodeAudioData(ab);
        } catch { return null; }
      })());
    }
    return decoded.get(url);
  };
  let any = false;
  for (const { url, o } of clips) {
    const buf = await decode(url);
    if (!buf) continue;
    const ss = Math.max(0, num(o.source_start, 0));
    const se = Math.min(buf.duration, num(o.source_end, buf.duration));
    const st = Math.max(0, num(o.start, 0));
    const len = Math.min(se - ss, num(o.end, st + (se - ss)) - st, duration - st);
    if (len <= 0.01) continue;
    let vol = Math.max(0, num(o.volume, 1));
    if (o.normalize) {
      let peak = 0;
      for (let c = 0; c < buf.numberOfChannels; c++) {
        const d = buf.getChannelData(c), a = Math.floor(ss * buf.sampleRate), b = Math.min(d.length, Math.floor(se * buf.sampleRate));
        for (let i = a; i < b; i += 4) { const v = Math.abs(d[i]); if (v > peak) peak = v; }
      }
      if (peak > 0.001) vol *= Math.min(8, 0.89 / peak);
    }
    const fin = Math.max(0, num(o.fade_in, 0)), fout = Math.max(0, num(o.fade_out, 0));
    const src = ctx.createBufferSource(); src.buffer = buf;
    let node = src;
    if (o.denoise) { const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 90; node.connect(hp); node = hp; }
    if (o.enhance) { const cp = ctx.createDynamicsCompressor(); cp.threshold.value = -20; cp.ratio.value = 3; node.connect(cp); node = cp; }
    const g = ctx.createGain();
    g.gain.setValueAtTime(fin > 0.01 ? 0 : vol, st);
    if (fin > 0.01) g.gain.linearRampToValueAtTime(vol, st + Math.min(fin, len));
    if (fout > 0.01) { g.gain.setValueAtTime(vol, st + Math.max(0, len - fout)); g.gain.linearRampToValueAtTime(0, st + len); }
    node.connect(g).connect(ctx.destination);
    src.start(st, ss, len);
    any = true;
  }
  return { buffer: await ctx.startRendering(), silent: !any };
}

async function encodeAudio(buf, muxer, pick, signal) {
  let err = null;
  const ae = new window.AudioEncoder({ output: (chunk, meta) => muxer.addAudioChunk(chunk, meta), error: (e) => { err = e; } });
  ae.configure(pick.config);
  const ch = buf.numberOfChannels, chans = Array.from({ length: ch }, (_, i) => buf.getChannelData(i));
  for (let off = 0; off < buf.length; off += SR) {
    abortIf(signal);
    if (err) throw err;
    const n = Math.min(SR, buf.length - off);
    const data = new Float32Array(n * ch);
    for (let c = 0; c < ch; c++) data.set(chans[c].subarray(off, off + n), c * n);
    const ad = new window.AudioData({ format: "f32-planar", sampleRate: SR, numberOfFrames: n, numberOfChannels: ch, timestamp: Math.round((off / SR) * 1e6), data });
    ae.encode(ad); ad.close();
  }
  await ae.flush(); ae.close();
  if (err) throw err;
}

async function loadImage(url) {
  const img = new Image(); img.crossOrigin = "anonymous"; img.src = url;
  try { await img.decode(); } catch { /* missing image → skipped when drawing */ }
  return img;
}

async function verifyPlayable(blob) {
  const url = URL.createObjectURL(blob);
  const v = document.createElement("video");
  v.muted = true; v.preload = "auto"; v.src = url;
  try {
    const ok = await new Promise((resolve) => {
      const t = setTimeout(() => resolve(true), 15000); // can't tell in time → don't block the download
      v.onloadeddata = () => { clearTimeout(t); resolve(!!v.videoWidth); };
      v.onerror = () => { clearTimeout(t); resolve(false); };
    });
    if (!ok) throw new Error("VERIFY_NO_VIDEO");
  } finally { v.src = ""; URL.revokeObjectURL(url); }
}

// Tiny probe: is this drawable rendering as (near) pure black?
const probe = (() => {
  let c = null, x = null;
  return (d) => {
    if (!c) { c = document.createElement("canvas"); c.width = 24; c.height = 24; x = c.getContext("2d", { willReadFrequently: true }); }
    x.clearRect(0, 0, 24, 24);
    try { x.drawImage(d, 0, 0, 24, 24); } catch { return true; }
    let px;
    try { px = x.getImageData(0, 0, 24, 24).data; } catch { return false; }
    let m = 0;
    for (let i = 0; i < px.length; i += 4) m = Math.max(m, px[i], px[i + 1], px[i + 2]);
    return m < 12;
  };
})();

// mode: "burned" (video + audio + captions) | "caption_only" (same picture, no audio)
// Hardware-decoded frames that draw as black (seen on some GPUs/drivers) are detected and that
// source is re-rendered through the browser's own <video> decoding — the path the preview uses.
export async function exportTimelineOnDevice(opts) {
  const forceElement = new Set();
  for (let attempt = 0; attempt < 4; attempt++) {
    try { return await renderOnce(opts, forceElement); }
    catch (e) {
      if (e?.code !== "DEMUX_BLACK") throw e;
      forceElement.add(e.url);
      opts.onProgress?.({ progress: 2, stage: "Switching to compatible video decoding" });
    }
  }
  throw new Error("SOURCE_DECODE_FAILED");
}

async function renderOnce({ mode = "burned", videoUrl, mediaTracks, layers, config, segments, width, height, fps,
  quality, duration, fitMode = "cover", letterbox = "black", signal, onProgress }, forceElement) {
  const t0 = performance.now();
  const W = width - (width % 2), H = height - (height % 2);
  const dur = Math.max(0.1, duration || 3);
  const say = (progress, stage, extra) => onProgress?.({ progress: Math.round(progress), stage, ...extra });

  say(1, "Checking your device");
  const vpick = await pickVideo(W, H, fps, Math.round(W * H * fps * (BPP[quality] || BPP.High)));
  if (!vpick) throw new Error("DEVICE_UNSUPPORTED");
  const apick = mode === "burned" ? await pickAudio() : null;

  // ---- load every source once (video files, images, fonts) ----
  const tracks = mediaTracks || [];
  const baseClip = { start: 0, end: dur, source_start: 0, source_end: dur };
  const urls = new Set();
  const anyVideo = tracks.some((tr) => tr.type === "video" && (tr.objects || []).length);
  if (!anyVideo && videoUrl) urls.add(videoUrl);
  videoClips(tracks).forEach(({ tr, o }) => { if (!tr.hidden && !o.hidden) urls.add(clipSourceUrl(o, videoUrl)); });
  const bg = backgroundObject(tracks);
  if (bg?.bgType === "video" && (bg.asset_url || bg.assetUrl)) urls.add(absUrl(bg.asset_url || bg.assetUrl));
  const list = [...urls].filter(Boolean);
  const buffers = new Map(), demux = new Map(), loaded = new Map();
  const loadPct = () => (list.length ? [...loaded.values()].reduce((a, b) => a + b, 0) / list.length : 1);
  await Promise.all(list.map(async (u) => {
    loaded.set(u, 0);
    const buf = await fetchWithProgress(u, (f) => { loaded.set(u, f); say(1 + loadPct() * 8, "Loading your media"); });
    buffers.set(u, buf);
    demux.set(u, forceElement.has(u) ? null : await loadDemux(buf).catch(() => null));
  }));
  abortIf(signal);
  const images = new Map();
  const imgUrls = imageClips(tracks).map(({ o }) => absUrl(o.asset_url)).filter(Boolean);
  if (bg?.bgType === "image" && (bg.asset_url || bg.assetUrl)) imgUrls.push(absUrl(bg.asset_url || bg.assetUrl));
  await Promise.all([...new Set(imgUrls)].map(async (u) => images.set(u, await loadImage(u))));
  const fams = new Set([config?.fontFamily, config?.fontFamilyB, ...Object.values(config?.wordStyles || {}).map((w) => w?.fontFamily)]);
  (layers || []).forEach((l) => (l.objects || []).forEach((o) => fams.add(o.style?.fontFamily)));
  await ensureTextFonts(collectCaptionText(layers, segments), [...fams].filter(Boolean)).catch(() => {});
  const bgDurations = new Map();
  if (bg?.bgType === "video") {
    const dm = demux.get(absUrl(bg.asset_url || bg.assetUrl));
    if (dm?.samples.length) { const l = dm.samples[dm.samples.length - 1]; bgDurations.set(bg, l.t + l.dur); }
  }
  say(10, "Rendering video on your device");

  // ---- audio mixes in parallel with the picture ----
  let silent = true;
  const audioPromise = apick
    ? renderTimelineAudio({ clips: timelineAudioClips(tracks, videoUrl, dur), duration: dur, buffers }).then((a) => { silent = a.silent; return a.buffer; })
    : null;
  audioPromise?.catch(() => {});

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({ target, fastStart: "in-memory", firstTimestampBehavior: "offset",
    video: { codec: vpick.mux, width: W, height: H, frameRate: fps },
    ...(apick ? { audio: { codec: apick.mux, numberOfChannels: 2, sampleRate: SR } } : {}) });
  let encErr = null;
  const encoder = new window.VideoEncoder({ output: (chunk, meta) => muxer.addVideoChunk(chunk, meta), error: (e) => { encErr = e; } });
  encoder.configure(vpick.config);

  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d", { alpha: false });
  const s = { mediaTracks: tracks, videoUrl, duration: dur, layers, config, segments, fitMode, letterbox, images, bgDurations };
  const readers = new Map(); // clip object → { r, last }
  const readerFor = async (n, i) => {
    let e = readers.get(n.key);
    if (!e) {
      const dm = demux.get(n.url);
      e = { r: dm ? createClipReader(dm) : await createSeekReader(n.url), last: i, demux: !!dm, probes: 0 };
      readers.set(n.key, e);
    }
    e.last = i;
    return e;
  };
  // Check a few decoded frames per demuxed source; if they're black but the <video> element
  // shows a picture at the same moment, restart with element decoding for that source.
  const checkBlack = async (n, e, frame) => {
    if (!e.demux || e.probes >= 3) return;
    e.probes++;
    if (!frame) throw Object.assign(new Error("DEMUX_BLACK"), { code: "DEMUX_BLACK", url: n.url });
    if (!probe(frame)) { e.probes = 3; return; }
    const er = await createSeekReader(n.url).catch(() => null);
    if (!er) return;
    const ef = await er.frameAt(n.srcT).catch(() => null);
    const elementBlack = !ef || probe(ef);
    er.close();
    if (!elementBlack) throw Object.assign(new Error("DEMUX_BLACK"), { code: "DEMUX_BLACK", url: n.url });
  };
  const sources = new Set();
  const total = Math.max(1, Math.round(dur * fps));
  const gop = Math.max(1, Math.round(fps * 2));
  try {
    for (let i = 0; i < total; i++) {
      abortIf(signal);
      if (encErr) throw encErr;
      const t = i / fps;
      const need = frameNeeds(s, t, baseClip);
      const frames = {};
      for (const k of ["bg", "prev", "clip"]) {
        if (!need[k]?.url) continue;
        const e = await readerFor(need[k], i);
        frames[k] = await e.r.frameAt(need[k].srcT);
        sources.add(e.demux ? "webcodecs" : "element");
        if (k === "clip" && (e.probes === 0 || i % (fps * 2) === 0)) await checkBlack(need[k], e, frames[k]);
      }
      drawTimelineFrame(ctx, t, W, H, s, need, frames);
      const vf = new window.VideoFrame(canvas, { timestamp: Math.round(t * 1e6), duration: Math.round(1e6 / fps) });
      encoder.encode(vf, { keyFrame: i % gop === 0 });
      vf.close();
      while (encoder.encodeQueueSize > 6) await new Promise((r) => setTimeout(r, 2));
      for (const [k, e] of readers) if (i - e.last > fps) { e.r.close(); readers.delete(k); } // free decoders of finished clips
      if (i % 3 === 0 || i === total - 1) say(10 + ((i + 1) / total) * 86, "Rendering video on your device", { frame: i + 1, totalFrames: total });
    }
    await encoder.flush();
    if (encErr) throw encErr;
    encoder.close();
    if (audioPromise) { say(97, "Finishing audio"); await encodeAudio(await audioPromise, muxer, apick, signal); }
    muxer.finalize();
  } finally {
    try { if (encoder.state !== "closed") encoder.close(); } catch { /* ignore */ }
    readers.forEach((e) => e.r.close());
  }
  const blob = new Blob([target.buffer], { type: "video/mp4" });
  say(98, "Checking the final video");
  await verifyPlayable(blob);
  const totalMs = Math.round(performance.now() - t0);
  return { blob, width: W, height: H, fps, duration: dur, audio: !!apick && !silent, codec: vpick.codec, audioCodec: apick?.codec || null,
    compat: !!vpick.std && (!apick || !!apick.std), frameSource: [...sources].join("+") || "none", hardware: vpick.hw, frames: total, encodeFps: Math.round((total / totalMs) * 1000), totalMs };
}
